import { CellStyle, ColDef, ICellRendererParams, RowStyle, ValueFormatterParams } from 'ag-grid-enterprise'

import { cn } from '@fortem-monorepo/js-utilities'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent } from '@fortem-monorepo/react-spa'
import { Badge } from '@fortem-monorepo/react-ui'
import { ICG2Guidelines, ICGG2Report } from '@fortem-monorepo/web-sdk'
import { ContentBlocks } from '@fortem-monorepo/web-sdk'

import { DataGridColumnDef } from '@/components/content-blocks/blocks/data-grid/types'
import { applyBackendColumnMetadata } from '@/components/content-blocks/blocks/data-grid/utils'
import {
  getCellStatusBadgeTheme,
  getCellStatusLabel,
  getValueColumn,
} from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/breaches-utils'
import { formatNumber } from '@/utilities/format'

import { getGuidelineValueScale } from './guideline-value-scale'

export interface GuidelineRowEvaluationsProps {
  result: ICGG2Report.GuidelineResult
  sourceBlock: ContentBlocks.DataGridContentBlock | null
}

type EvaluationGridRow = Record<string, unknown> & {
  __status: ICG2Guidelines.GuidelineStatus | null
  __breach: string | null
  __messages: string
}

const rowStyleByStatus: Partial<Record<ICG2Guidelines.GuidelineStatus, RowStyle>> = {
  failed: { backgroundColor: '#eff6ff' },
  hard: { backgroundColor: '#fef2f2' },
  soft: { backgroundColor: '#fffbeb' },
}

const valueColumnTextStyleByStatus: Partial<Record<ICG2Guidelines.GuidelineStatus, CellStyle>> = {
  hard: { color: '#991b1b', fontWeight: 600 },
  soft: { color: '#92400e', fontWeight: 600 },
  ok: { color: '#32a852', fontWeight: 600 },
}

const formatBreach = (
  breach: NonNullable<ICGG2Report.GuidelineResult['rows'][number]['result']>['breach'],
  thresholdType: ICGG2Report.GuidelineResult['guideline']['threshold_type'],
  valueScale: number,
) => {
  if (!breach) return null

  return `${getCellStatusLabel(breach.severity)} ${breach.bound} ${formatNumber(breach.threshold * valueScale, {
    type: thresholdType,
    suffix: thresholdType === 'percentage' ? '%' : '',
  })}`
}

export const GuidelineRowEvaluations: React.FC<GuidelineRowEvaluationsProps> = ({
  result,
  sourceBlock,
}) => {
  if (!result.rows.length) return null

  const valueColumn = getValueColumn(result)
  const identifierColumns = Object.keys(result.guideline.identifiers ?? {})
  const sourceColumns = Array.from(
    result.rows.reduce((columns, row) => {
      Object.keys(row.row).forEach((column) => columns.add(column))
      return columns
    }, new Set<string>()),
  )
  const restColumns = sourceColumns.filter((column) => {
    return !identifierColumns.includes(column) && column !== valueColumn
  })
  const orderedSourceColumns = [...identifierColumns, valueColumn, ...restColumns].filter(
    (column, index, columns) => {
      return columns.indexOf(column) === index && result.rows.some((row) => column in row.row)
    },
  )

  const rowData = result.rows
    .map<EvaluationGridRow>((row) => {
      const evaluation = row.result

      return {
        ...Object.fromEntries(orderedSourceColumns.map((key) => [key, row.row[key]])),
        __status: evaluation?.status ?? null,
        __breach: formatBreach(
          evaluation?.breach ?? null,
          result.guideline.threshold_type,
          getGuidelineValueScale(result.guideline, row.row),
        ),
        __messages: evaluation?.messages.join(', ') ?? '',
      }
    })
    .sort((a, b) => {
      const left = typeof a[valueColumn] === 'number' ? a[valueColumn] : Number.NEGATIVE_INFINITY
      const right = typeof b[valueColumn] === 'number' ? b[valueColumn] : Number.NEGATIVE_INFINITY

      return right - left
    })

  const identifierColumnDefs: ColDef<EvaluationGridRow>[] = identifierColumns
    .filter((column) => result.rows.some((row) => column in row.row))
    .map((field) => ({
      field,
      minWidth: 120,
    }))

  const restColumnDefs: ColDef<EvaluationGridRow>[] = restColumns
    .filter((column) => result.rows.some((row) => column in row.row))
    .map((field) => ({
      field,
      minWidth: 120,
    }))

  const sourceColumnDefs: ColDef<EvaluationGridRow>[] = [
    ...identifierColumnDefs,
    {
      field: valueColumn,
      minWidth: 140,
      ...(result.guideline.table === 'summary.headline' && valueColumn === 'value'
        ? {
            valueFormatter: ({ value, data }: ValueFormatterParams<EvaluationGridRow>) =>
              typeof value === 'number'
                ? formatNumber(data?.unit === 'pct' ? value * 100 : value, {
                    suffix: data?.unit === 'pct' ? '%' : '',
                  })
                : value,
          }
        : {}),
      cellStyle: (params) => {
        const status = params.data?.__status as ICG2Guidelines.GuidelineStatus | null | undefined

        return status ? valueColumnTextStyleByStatus[status] : undefined
      },
    },
    ...restColumnDefs,
  ]

  const enrichedSourceColumnDefs: DataGridColumnDef<EvaluationGridRow>[] = sourceBlock
    ? applyBackendColumnMetadata<EvaluationGridRow>(
        sourceColumnDefs,
        sourceBlock as ContentBlocks.DataGridContentBlock<EvaluationGridRow>,
      )
    : sourceColumnDefs

  const columnDefs: DataGridColumnDef<EvaluationGridRow>[] = [
    {
      field: '__status',
      headerName: 'Status',
      width: 115,
      pinned: 'left',
      cellRenderer: (
        params: ICellRendererParams<EvaluationGridRow, ICG2Guidelines.GuidelineStatus | null>,
      ) => {
        if (!params.value) return null

        return (
          <Badge theme={getCellStatusBadgeTheme(params.value)} variant={'outline'}>
            {getCellStatusLabel(params.value)}
          </Badge>
        )
      },
    },
    {
      field: '__breach',
      headerName: 'Breach',
      minWidth: 150,
    },
    ...enrichedSourceColumnDefs,
    {
      field: '__messages',
      headerName: 'Messages',
      pinned: 'right',
      minWidth: 220,
      flex: 1,
    },
  ]

  return (
    <Widget>
      <WidgetContent
        className={cn({
          'h-100': result.rows.length >= 10,
        })}
      >
        <CustomAgGrid
          domLayout={result.rows.length >= 10 ? 'normal' : 'autoHeight'}
          rowData={rowData}
          columnDefs={columnDefs}
          suppressFieldDotNotation
          headerHeight={32}
          rowHeight={34}
          suppressCellFocus
          getRowStyle={(params: any) => {
            const status = params.data?.__status as
              ICG2Guidelines.GuidelineStatus | null | undefined

            return status ? rowStyleByStatus[status] : undefined
          }}
        />
      </WidgetContent>
    </Widget>
  )
}
